import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showSuccess, showError } from '../utils/alerts';
import { companyService } from '../services/companyService';

interface EmailSettings {
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_password: string;
  from_name: string;
  from_email: string;
}

const emailValidationSchema = yup.object({
  smtp_host: yup.string().required('El servidor SMTP es requerido'),
  smtp_port: yup.number().min(1).max(65535).required('El puerto es requerido'),
  smtp_secure: yup.boolean().required(),
  smtp_user: yup.string().email('Email inválido').required('El usuario SMTP es requerido'),
  smtp_password: yup.string().required('La contraseña es requerida'),
  from_name: yup.string().required('El nombre remitente es requerido'),
  from_email: yup.string().email('Email inválido').required('El email remitente es requerido')
});

const EmailConfiguration: React.FC = () => {
  const queryClient = useQueryClient();
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<EmailSettings>({
    resolver: yupResolver(emailValidationSchema),
    defaultValues: {
      smtp_host: '',
      smtp_port: 587,
      smtp_secure: false,
      smtp_user: '',
      smtp_password: '',
      from_name: '',
      from_email: ''
    }
  });

  // Get email settings
  const { data: emailSettings, isLoading } = useQuery({
    queryKey: ['email-settings'],
    queryFn: companyService.getEmailSettings
  });

  // Load email settings into form
  useEffect(() => {
    if (emailSettings) {
      reset({
        smtp_host: emailSettings.smtp_host || '',
        smtp_port: emailSettings.smtp_port || 587,
        smtp_secure: emailSettings.smtp_secure || false,
        smtp_user: emailSettings.smtp_user || '',
        smtp_password: emailSettings.smtp_password === '***' ? '' : emailSettings.smtp_password || '',
        from_name: emailSettings.from_name || '',
        from_email: emailSettings.from_email || emailSettings.smtp_user || ''
      });
    }
  }, [emailSettings, reset]);

  // Update email settings mutation
  const updateEmailMutation = useMutation({
    mutationFn: companyService.updateEmailSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-settings'] });
      showSuccess('Configuración de email actualizada exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error al actualizar la configuración de email';
      showError(message);
    }
  });

  // Test email mutation
  const testEmailMutation = useMutation({
    mutationFn: companyService.testEmailSettings,
    onSuccess: () => {
      showSuccess(`Email de prueba enviado exitosamente a ${testEmail}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error enviando email de prueba';
      showError(message);
    }
  });

  const onSubmit = async (data: EmailSettings) => {
    // Trim all string values to prevent issues
    const trimmedData = {
      ...data,
      smtp_host: data.smtp_host?.trim(),
      smtp_user: data.smtp_user?.trim(),
      smtp_password: data.smtp_password?.trim(),
      from_name: data.from_name?.trim(),
      from_email: data.from_email?.trim()
    };
    await updateEmailMutation.mutateAsync(trimmedData);
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      showError('Ingrese un email para la prueba');
      return;
    }
    await testEmailMutation.mutateAsync({ testEmail });
  };

  const isConfigured = emailSettings?.isConfigured;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Configuración de Email</h2>
            <p className="text-gray-600 text-sm mt-1">
              Configure el servidor SMTP para enviar cotizaciones por email
            </p>
          </div>
          {isConfigured && (
            <div className="flex items-center text-green-600">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium">Configurado</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* SMTP Configuration */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración SMTP</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Servidor SMTP <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('smtp_host')}
                  className="form-input"
                  placeholder="smtp.gmail.com"
                />
                {errors.smtp_host && (
                  <p className="form-error">{errors.smtp_host.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Gmail: smtp.gmail.com | Outlook: smtp.office365.com
                </p>
              </div>

              <div>
                <label className="form-label">
                  Puerto <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  {...register('smtp_port')}
                  className="form-input"
                  placeholder="587"
                />
                {errors.smtp_port && (
                  <p className="form-error">{errors.smtp_port.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Gmail: 465 (SSL) o 587 (TLS) | Outlook: 587 (TLS) | Otros: 25 (sin cifrado)
                </p>
              </div>

              <div>
                <label className="form-label">
                  Usuario SMTP <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register('smtp_user')}
                  className="form-input"
                  placeholder="tu-email@gmail.com"
                />
                {errors.smtp_user && (
                  <p className="form-error">{errors.smtp_user.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Contraseña SMTP <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('smtp_password')}
                    className="form-input pr-10"
                    placeholder="Contraseña de aplicación"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {showPassword ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      )}
                    </svg>
                  </button>
                </div>
                {errors.smtp_password && (
                  <p className="form-error">{errors.smtp_password.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Para Gmail: Use una contraseña de aplicación
                </p>
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('smtp_secure')}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Usar conexión segura (SSL/TLS)</span>
              </label>
            </div>
          </div>

          {/* Sender Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Información del Remitente</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Nombre del Remitente <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('from_name')}
                  className="form-input"
                  placeholder="Mi Empresa"
                />
                {errors.from_name && (
                  <p className="form-error">{errors.from_name.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Email del Remitente <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register('from_email')}
                  className="form-input"
                  placeholder="contacto@miempresa.com"
                />
                {errors.from_email && (
                  <p className="form-error">{errors.from_email.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="border-t pt-6 flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              disabled={isSubmitting || updateEmailMutation.isPending}
              className="btn-primary"
            >
              {(isSubmitting || updateEmailMutation.isPending) ? 'Guardando...' : 'Guardar Configuración'}
            </button>

            {isConfigured && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Email para prueba"
                  className="form-input flex-1"
                />
                <button
                  type="button"
                  onClick={handleTestEmail}
                  disabled={testEmailMutation.isPending || !testEmail}
                  className="btn-outline whitespace-nowrap"
                >
                  {testEmailMutation.isPending ? 'Enviando...' : 'Enviar Prueba'}
                </button>
              </div>
            )}
          </div>
        </form>

        {/* Setup Instructions */}
        <div className="mt-8 space-y-4">
          {/* Gmail Setup */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">📧 Configuración para Gmail:</h4>
            <ol className="text-sm text-blue-800 space-y-2 ml-4 list-decimal">
              <li>Vaya a su cuenta de Google → Seguridad</li>
              <li>Active la verificación en 2 pasos</li>
              <li>
                Genere una contraseña de aplicación:
                <br />
                <a 
                  href="https://myaccount.google.com/apppasswords" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Crear contraseña de aplicación
                </a>
              </li>
              <li>Use esa contraseña de aplicación en el campo "Contraseña SMTP"</li>
              <li>
                Configuración SMTP:
                <div className="mt-1 text-xs">
                  • Servidor: <code className="bg-blue-200 px-1 rounded">smtp.gmail.com</code><br/>
                  • Puerto: <code className="bg-blue-200 px-1 rounded">465</code> (SSL) o <code className="bg-blue-200 px-1 rounded">587</code> (TLS)<br/>
                  • SSL/TLS: ✓ Activado
                </div>
              </li>
            </ol>
            <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-700">
              <strong>Importante:</strong> NO use su contraseña regular de Gmail. Debe usar una contraseña de aplicación específica.
            </div>
          </div>

          {/* Outlook Setup */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-900 mb-2">📧 Configuración para Outlook/Hotmail:</h4>
            <ul className="text-sm text-green-800 space-y-1 ml-4 list-disc">
              <li>Servidor: smtp.office365.com</li>
              <li>Puerto: 587, SSL/TLS: ✓</li>
              <li>Use su email y contraseña completos</li>
            </ul>
          </div>

          {/* Tips and Troubleshooting */}
          <div className="space-y-3">
            {/* Success Tips */}
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 mb-2">✅ Configuración Recomendada:</h4>
              <ul className="text-sm text-green-800 space-y-1 ml-4 list-disc">
                <li><strong>Puerto 465 (SSL)</strong> - Más estable y seguro para Gmail</li>
                <li><strong>Puerto 587 (TLS)</strong> - Alternativa compatible con la mayoría de proveedores</li>
                <li>Siempre activar SSL/TLS para mayor seguridad</li>
              </ul>
            </div>

            {/* Common Issues */}
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">⚠️ Problemas Comunes:</h4>
              <ul className="text-sm text-yellow-800 space-y-1 ml-4 list-disc">
                <li>Evite espacios en blanco al inicio o final de los campos</li>
                <li>Verifique que su proveedor de internet no bloquee el puerto SMTP</li>
                <li>Para Gmail, use contraseña de aplicación, no su contraseña regular</li>
                <li>Asegúrese de que la verificación en 2 pasos esté activada (Gmail)</li>
                <li>Si el puerto 587 no funciona, pruebe con el puerto 465</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfiguration;